<template>
  <div class="comic-reader" :class="{ 'fullscreen-mode': isFullscreen, 'continuous-reader': viewMode === 'continuous' }">
    <!-- 顶部导航栏 -->
    <div class="reader-header" :class="{ 'hidden': isHeaderHidden }">
      <div class="page-info">
        <span>{{ currentPage }} / {{ totalPages }}</span>
      </div>
      <div class="chapter-info">
        <span>{{ comicTitle }}</span>
      </div>
      <!-- 章节选择列表 -->
      <div class="chapter-select">
        <span v-if="chapters.length === 0" style="color: #f00">无可用章节</span>
        <select :value="currentChapterId" @change="onChapterSelectChange">
          <option v-if="chapters.length === 0" disabled>暂无章节</option>
          <option v-for="chapter in chapters" :key="chapter.id" :value="chapter.id">
            {{ chapter.title }} {{ !chapter.is_free ? '(VIP)' : '' }}
          </option>
        </select>
        <span v-if="chapters.length" style="color: #888; font-size: 12px; margin-left: 8px;">共{{ chapters.length }}章</span>
      </div>
      
      <!-- 显示模式切换按钮 -->
      <div class="view-controls">
        <button @click="toggleViewMode" class="view-mode-btn">
          {{ viewMode === 'single' ? '长图模式' : '单页模式' }}
        </button>
        <button @click="toggleFullscreen" class="fullscreen-btn">
          {{ isFullscreen ? '退出全屏' : '全屏阅读' }}
        </button>
      </div>
    </div>

    <!-- VIP限制提示 -->
    <div v-if="showVipLimitMsg" class="vip-limit-message">
      <div class="vip-alert">
        <h3>VIP章节</h3>
        <p>当前章节仅对VIP会员开放</p>
        <div class="coins-info" v-if="userInfo && userInfo.coins !== undefined">
          <p>您当前的金币余额：<span class="coin-balance">{{ userInfo.coins }}</span></p>
          <p>本章节需要消费：<span class="coin-cost">{{ chapterInfo.price || 3 }}</span> 金币</p>
        </div>
        <div class="vip-buttons">
          <button @click="goToProfile" class="go-vip-btn">开通VIP</button>
          <button @click="buyChapterWithCoins" class="buy-btn">
            金币购买
          </button>
          <button @click="goBack" class="back-btn">返回</button>
        </div>
      </div>
    </div>

    <!-- 漫画内容区域 -->
    <div class="comic-content" ref="comicContent" @click="toggleHeader" :class="{ 'continuous-mode': viewMode === 'continuous' }">
      <!-- 单页模式 -->
      <template v-if="viewMode === 'single' && !showVipLimitMsg">
        <div v-if="chapterImages.length && currentPage <= totalPages" class="comic-page-wrapper">
          <img 
            :src="chapterImages[currentPage - 1]"
            class="comic-page" 
            @load="onImageLoad"
            @error="onImageError"
          />
          <span class="watermark watermark-left-bottom">格瑞创新</span>
          <span class="watermark watermark-right-bottom">格瑞创新</span>
        </div>
        <div v-else class="end-page">
          <h2>漫画已完结</h2>
          <p>感谢您的阅读！</p>
          <button class="restart-btn" @click="restartReading">重新开始</button>
        </div>
      </template>
      
      <!-- 长图模式（连续滚动） -->
      <div v-else-if="viewMode === 'continuous' && !showVipLimitMsg" class="continuous-view">
        <div 
          v-for="(img, index) in chapterImages" 
          :key="index"
          class="continuous-page-wrapper"
        >
          <img 
            :src="img"
            class="continuous-page" 
            @load="onContinuousImageLoad(index)"
            @error="onImageError"
          />
          <span class="watermark watermark-left-bottom">格瑞创新</span>
          <span class="watermark watermark-right-bottom">格瑞创新</span>
        </div>
        <div class="end-page">
          <h2>本章完</h2>
          <div class="chapter-nav-buttons">
            <button 
              v-if="getPrevChapterId()" 
              @click="goToChapter(getPrevChapterId())" 
              class="chapter-nav-btn"
            >
              上一章
            </button>
            <button 
              v-if="getNextChapterId()" 
              @click="goToChapter(getNextChapterId())" 
              class="chapter-nav-btn"
            >
              下一章
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 底部控制栏 -->
    <div class="reader-controls" v-if="viewMode === 'single'" :class="{ 'hidden': isHeaderHidden }">
      <button 
        class="nav-btn prev-btn" 
        @click="prevPage"
        :disabled="currentPage <= 1"
      >
        <span>← 上一页</span>
      </button>
      
      <div class="page-controls">
        <button class="page-btn" @click="goToPage(1)" :class="{ active: currentPage === 1 }">1</button>
        <button class="page-btn" @click="goToPage(2)" :class="{ active: currentPage === 2 }">2</button>
        <button class="page-btn" @click="goToPage(3)" :class="{ active: currentPage === 3 }">3</button>
        <span class="page-dots">...</span>
        <button class="page-btn" @click="goToPage(totalPages - 2)" :class="{ active: currentPage === totalPages - 2 }">{{ totalPages - 2 }}</button>
        <button class="page-btn" @click="goToPage(totalPages - 1)" :class="{ active: currentPage === totalPages - 1 }">{{ totalPages - 1 }}</button>
        <button class="page-btn" @click="goToPage(totalPages)" :class="{ active: currentPage === totalPages }">{{ totalPages }}</button>
      </div>
      
      <button 
        class="nav-btn next-btn" 
        @click="nextPage"
        :disabled="currentPage >= totalPages"
      >
        <span>下一页 →</span>
      </button>
    </div>

    <!-- 单页模式快捷键提示 -->
    <div v-if="viewMode === 'single'" class="shortcut-hint" :class="{ 'hidden': isHeaderHidden }">
      <span class="shortcut-item"><kbd>←</kbd><kbd>→</kbd> 上一页/下一页</span>
      <span class="shortcut-item"><kbd>Space</kbd> 下一页</span>
      <span class="shortcut-item"><kbd>Home</kbd> 首页</span>
      <span class="shortcut-item"><kbd>End</kbd> 末页</span>
    </div>

    <!-- 左下角功能按钮 -->
    <div class="reader-tools" :class="{ 'hidden': isHeaderHidden }">
      <button class="tool-btn report-btn" @click="openReportModal" title="投诉举报">
        <svg class="tool-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
        <span class="tool-text">投诉举报</span>
      </button>
    </div>

    <!-- 投诉举报弹窗 -->
    <div v-if="showReportModal" class="report-overlay" @click.self="closeReportModal">
      <div class="report-modal">
        <div class="report-header">
          <h3>投诉举报</h3>
          <button class="report-close" @click="closeReportModal">×</button>
        </div>
        <div class="report-body">
          <div class="report-info">
            <p><strong>漫画：</strong>{{ comicTitle || '未知' }}</p>
            <p><strong>章节：</strong>{{ chapterInfo?.title || '当前章节' }}</p>
            <p><strong>页码：</strong>{{ currentPage }}</p>
          </div>
          <div class="form-group">
            <label>举报类型</label>
            <select v-model="reportForm.type">
              <option value="">请选择举报类型</option>
              <option value="copyright">版权问题</option>
              <option value="content">内容违规</option>
              <option value="quality">画质/翻译问题</option>
              <option value="other">其他问题</option>
            </select>
          </div>
          <div class="form-group">
            <label>详细描述</label>
            <textarea v-model="reportForm.content" rows="4" placeholder="请详细描述您遇到的问题..."></textarea>
          </div>
          <div class="form-group">
            <label>联系方式（选填）</label>
            <input v-model="reportForm.contact" type="text" placeholder="手机号或邮箱，方便我们联系您">
          </div>
        </div>
        <div class="report-footer">
          <button class="cancel-btn" @click="closeReportModal">取消</button>
          <button class="submit-btn" @click="submitReport" :disabled="reportSubmitting">
            {{ reportSubmitting ? '提交中...' : '提交举报' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 加载提示 -->
    <div v-if="isLoading" class="loading-overlay">
      <div class="loading-spinner"></div>
      <p>加载中...</p>
    </div>
  </div>
</template>

<script>
import { getChapterList, getChapterDetail, getUserVipInfo, getUserInfo, buyChapter, BASE_URL, saveReadingProgress, getReadingProgress, ReadingProgressManager } from '../api/index';
import youthModeManager from '@/utils/youthMode';

export default {
  name: 'ComicReader',
  props: {
    id: {
      type: [String, Number],
      default: null
    },
    comicId: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      chapterId: null,
      internalComicId: null, // 使用此属性存储漫画ID
      currentChapterId: null,
      chapters: [],
      chapterImages: [],
      currentPage: 1,
      totalPages: 0,
      isLoading: false,
      loadedImagesCount: 0,
      viewMode: 'long', // 'page' 或 'long'
      showControls: true,
      isFullscreen: false,
      controlsTimeout: null,
      showVipLimitMsg: false,
      userVipStatus: {
        isVip: false,
        endDate: null
      },
      chapterInfo: null,
      comicInfo: null,
      comicTitle: '',
      accessInfo: null,
      userInfo: null,
      purchaseStatus: {
        inProgress: false,
        success: false,
        error: null
      },

      // 防止重复加载的标志
      isSelectingChapter: false,

      // 阅读进度相关
      progressSaveTimer: null,
      autoSaveInterval: 10000, // 10秒自动保存一次
      lastSavedPage: 0,
      isProgressLoaded: false,

      // 投诉举报
      showReportModal: false,
      reportSubmitting: false,
      reportForm: {
        type: '',
        content: '',
        contact: ''
      }
    }
  },
  async created() {
    // 获取章节ID，优先使用props中的id
    this.chapterId = this.id || this.$route.params.id;
    console.log('[ComicReader] 页面创建，章节ID:', this.chapterId, '类型:', typeof this.chapterId);
    console.log('[ComicReader] 路由参数:', JSON.stringify(this.$route.params));
    console.log('[ComicReader] Props参数:', { id: this.id, comicId: this.comicId });
    console.log('[ComicReader] 当前路由路径:', this.$route.path);
    
    // 尝试从props或路由参数中获取漫画ID
    if (this.comicId) {
      this.internalComicId = this.comicId;
      console.log('[ComicReader] 从props获取漫画ID:', this.internalComicId);
    } else if (this.$route.params.comicId) {
      this.internalComicId = this.$route.params.comicId;
      console.log('[ComicReader] 从路由参数获取漫画ID:', this.internalComicId);
    }
    
    // 尝试从URL中提取漫画ID和章节ID
    const urlMatch = this.$route.path.match(/\/reader\/(\d+)/);
    if (urlMatch && urlMatch[1]) {
      if (!this.chapterId) {
        this.chapterId = urlMatch[1];
        console.log('[ComicReader] 从URL提取章节ID:', this.chapterId);
      }
    }
    
    // 恢复阅读模式设置
    this.loadViewModeSettings();
    
    // 获取用户VIP状态
    await this.checkVipStatus();
    
    // 获取用户信息（包括金币余额）
    await this.fetchUserInfo();
    
    if (this.chapterId) {
      console.log('[ComicReader] 开始初始化章节内容流程，章节ID:', this.chapterId);

      // 首先设置当前章节ID
      this.currentChapterId = this.chapterId;
      console.log('[ComicReader] 设置当前章节ID:', this.currentChapterId);

      // 如果已经有漫画ID，先获取章节列表
      if (this.internalComicId) {
        console.log('[ComicReader] 已有漫画ID，先获取章节列表');
        await this.fetchChapters();
      }

      // 加载当前章节详情
      console.log('[ComicReader] 准备加载章节详情，章节ID:', this.chapterId);
      const chapterLoaded = await this.loadChapter(this.chapterId);
      console.log('[ComicReader] 章节加载结果:', chapterLoaded ? '成功' : '失败');

      // 如果成功获取漫画ID，再获取章节列表（如果之前没有获取过）
      if (chapterLoaded && this.comicInfo && this.comicInfo.id) {
        if (!this.internalComicId) {
          this.internalComicId = this.comicInfo.id;
          console.log('[ComicReader] 从章节详情获取漫画ID:', this.internalComicId);

          // 获取章节列表
          await this.fetchChapters();
        }
      } else {
        console.error('[ComicReader] 无法获取漫画ID，章节加载可能不完整');

        // 尝试从URL中获取漫画ID
        const comicIdMatch = this.$route.path.match(/\/comic\/(\d+)\/chapter/);
        if (comicIdMatch && comicIdMatch[1]) {
          this.internalComicId = comicIdMatch[1];
          console.log('[ComicReader] 从URL路径中提取漫画ID:', this.internalComicId);

          // 使用从URL提取的漫画ID获取章节列表
          await this.fetchChapters();
        } else {
          console.error('[ComicReader] 无法从URL中提取漫画ID');

          // 最后尝试直接获取章节列表，可能API会返回相关漫画信息
          if (this.chapterId) {
            console.log('[ComicReader] 尝试通过章节ID获取相关信息');
            await this.fetchChapterInfo();
          }
        }
      }
    } else {
      console.error('[ComicReader] 初始化失败：无效的章节ID');
    }
  },
  mounted() {
    this.addKeyboardListeners();
    this.restoreReadingProgress();
    document.addEventListener('fullscreenchange', this.handleFullscreenChange);
    this.startAutoSaveProgress();
    this.applyBodyClass();
  },
  beforeUnmount() {
    this.removeKeyboardListeners();
    document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
    this.saveCurrentProgress();
    this.stopAutoSaveProgress();
    document.body.classList.remove('reader-continuous-mode');
  },
  methods: {
    async fetchChapters() {
      try {
        if (!this.internalComicId) {
          console.error('[ComicReader] 无法获取章节列表：漫画ID为空');
          return;
        }

        // 未成年人保护：青少年模式开启时仅允许访问适龄内容池
        await youthModeManager.ensureReady();
        if (!youthModeManager.isComicAllowed(this.internalComicId)) {
          youthModeManager.notifyContentBlocked();
          this.$router.replace('/');
          return;
        }

        console.log('[ComicReader] 开始加载章节列表，漫画ID:', this.internalComicId);
        console.log('[ComicReader] 调用API: getChapterList，参数:', { comic_id: this.internalComicId });
        
        const res = await getChapterList({ comic_id: this.internalComicId });
        
        console.log('[ComicReader] 章节列表API原始返回:', JSON.stringify(res.data));
        
        if (res.data && res.data.code === 1) {
          console.log('[ComicReader] 章节列表获取成功，状态码:', res.data.code);
          console.log('[ComicReader] 章节列表详细数据:', JSON.stringify(res.data.data));
          
          this.chapters = res.data.data.list || [];
          console.log('[ComicReader] 章节数量:', this.chapters.length);
          
          if (this.chapters.length > 0) {
            // 只有在没有设置当前章节ID时才使用第一个章节
            if (!this.currentChapterId) {
              this.currentChapterId = this.chapters[0].id;
              console.log('[ComicReader] 默认选中章节ID:', this.currentChapterId);
            } else {
              console.log('[ComicReader] 保持当前章节ID:', this.currentChapterId);
            }

            // 记录所有章节的基本信息
            console.log('[ComicReader] 所有章节概览:');
            this.chapters.forEach((chapter, index) => {
              console.log(`[ComicReader] 章节[${index}]:`, chapter.id, chapter.title,
                        '免费状态:', chapter.is_free ? '免费' : '付费');
            });
          } else {
            console.warn('[ComicReader] 该漫画无章节，返回空列表');
          }
        } else {
          console.error('[ComicReader] 章节列表API返回异常', JSON.stringify(res.data));
          // 如果API返回异常，创建一个基本的章节列表
          this.createFallbackChapterList();
        }
      } catch (e) {
        console.error('[ComicReader] 章节列表加载失败', e);
        console.error('[ComicReader] 错误详情:', e.message, e.stack);
        // 如果请求失败，创建一个基本的章节列表
        this.createFallbackChapterList();
      }
    },
    createFallbackChapterList() {
      console.log('[ComicReader] 创建备用章节列表');
      if (this.chapterId) {
        // 创建一个包含当前章节的基本列表
        this.chapters = [
          {
            id: parseInt(this.chapterId),
            title: `第${this.chapterId}话`,
            is_free: true,
            price: 0,
            comic_id: this.internalComicId
          }
        ];

        // 添加一些相邻的章节
        for (let i = 1; i <= 5; i++) {
          const prevId = parseInt(this.chapterId) - i;
          const nextId = parseInt(this.chapterId) + i;

          if (prevId > 0) {
            this.chapters.unshift({
              id: prevId,
              title: `第${prevId}话`,
              is_free: true,
              price: 0,
              comic_id: this.internalComicId
            });
          }

          this.chapters.push({
            id: nextId,
            title: `第${nextId}话`,
            is_free: true,
            price: 0,
            comic_id: this.internalComicId
          });
        }

        console.log('[ComicReader] 备用章节列表创建完成，章节数量:', this.chapters.length);
      }
    },
    async loadChapter(chapterId) {
      console.log('[ComicReader] 开始加载章节详情，章节ID:', chapterId, '类型:', typeof chapterId);
      this.isLoading = true;
      try {
        if (!chapterId) {
          console.error('[ComicReader] 加载章节失败: 无效的章节ID');
          this.isLoading = false;
          return false;
        }
        
        // 确保章节ID是字符串类型
        const chapterIdStr = String(chapterId);
        console.log('[ComicReader] 转换后的章节ID:', chapterIdStr, '类型:', typeof chapterIdStr);
        
        console.log('[ComicReader] 调用API: getChapterDetail', chapterIdStr);
        const res = await getChapterDetail(chapterIdStr);
        console.log('[ComicReader] API返回原始数据:', JSON.stringify(res.data));
        
        if (res.data && res.data.code === 1) {
          console.log('[ComicReader] API返回成功，状态码:', res.data.code);
          
          const data = res.data.data;
          if (!data) {
            console.error('[ComicReader] API返回数据为空');
            this.isLoading = false;
            return false;
          }
          
          console.log('[ComicReader] 章节详情数据结构:', Object.keys(data).join(', '));
          
          // 打印章节ID相关信息
          if (data.chapter) {
            console.log('[ComicReader] 返回的章节ID:', data.chapter.id, '类型:', typeof data.chapter.id);
            console.log('[ComicReader] 章节标题:', data.chapter.title);
            console.log('[ComicReader] 章节完整信息:', JSON.stringify(data.chapter));
          } else {
            console.error('[ComicReader] API返回数据中没有章节信息');
            this.isLoading = false;
            return false;
          }
          
          // 检查是否有漫画信息
          if (!data.comic || !data.comic.id) {
            console.error('[ComicReader] 漫画信息缺失或无效:', data.comic);
            // 尝试从章节信息中获取漫画ID
            if (data.chapter && data.chapter.comic_id) {
              this.comicInfo = { 
                id: data.chapter.comic_id,
                title: data.chapter.comic_title || '未知漫画'
              };
              console.log('[ComicReader] 从章节信息中获取漫画ID:', this.comicInfo.id);
            } else {
              console.error('[ComicReader] 无法从章节信息中获取漫画ID');
              this.isLoading = false;
              return false;
            }
          } else {
            // 保存漫画信息
            this.comicInfo = data.comic;
            console.log('[ComicReader] 从API返回中获取漫画信息:', JSON.stringify(this.comicInfo));
          }
          
          // 检查章节是否需要VIP
          const isVipChapter = data.chapter && !data.chapter.is_free;
          console.log('[ComicReader] 章节VIP状态:', isVipChapter ? 'VIP章节' : '免费章节');
          
          // 检查用户是否有访问权限
          const canAccess = data.accessInfo && data.accessInfo.canAccess;
          console.log('[ComicReader] 用户访问权限:', canAccess ? '可访问' : '不可访问');
          
          // 如果章节需要VIP但用户不是VIP，且用户没有购买过此章节
          if (isVipChapter && !this.userVipStatus.isVip && !canAccess) {
            console.log('[ComicReader] 非VIP用户尝试访问VIP章节');
            // 记录章节信息（包括价格）以便购买
            if (data.chapter) {
              this.chapterInfo = data.chapter;
            }
            this.showVipLimitMsg = true;
            this.isLoading = false;
            return false;
          } else {
            this.showVipLimitMsg = false;
          }
          
          // 设置漫画标题
          if (data.comic && data.comic.title) {
            this.comicTitle = data.comic.title;
          } else if (data.chapter && data.chapter.comic_title) {
            this.comicTitle = data.chapter.comic_title;
          }
          console.log('[ComicReader] 设置漫画标题:', this.comicTitle);
          
          // 处理章节数据
          return await this.processChapterData(data);
        } else {
          console.error('[ComicReader] API返回错误:', JSON.stringify(res.data));
          this.isLoading = false;
          return false;
        }
      } catch (e) {
        console.error('[ComicReader] 加载章节失败:', e);
        console.error('[ComicReader] 错误详情:', e.message);
        console.error('[ComicReader] 错误堆栈:', e.stack);
        this.isLoading = false;
        return false;
      }
    },
    // 从章节详情数据中提取图片
    async processChapterData(data) {
      console.log('[ComicReader] 处理章节数据');
      try {
        if (!data) {
          console.error('[ComicReader] 章节数据为空');
          this.isLoading = false;
          return false;
        }
        
        // 保存章节信息
        if (data.chapter) {
          this.chapterInfo = data.chapter;
          console.log('[ComicReader] 章节信息:', JSON.stringify(this.chapterInfo));
          
          // 如果章节中包含漫画ID但comicInfo中没有，则使用章节中的漫画ID
          if (data.chapter.comic_id && (!this.comicInfo || !this.comicInfo.id)) {
            this.comicInfo = this.comicInfo || {};
            this.comicInfo.id = data.chapter.comic_id;
            this.comicInfo.title = data.chapter.comic_title || '未知漫画';
            console.log('[ComicReader] 从章节信息中补充漫画ID:', this.comicInfo.id);
          }
        } else {
          console.error('[ComicReader] 无法获取章节信息');
          this.isLoading = false;
          return false;
        }
        
        // 保存漫画信息
        if (data.comic) {
          // 如果已经有漫画信息，合并而不是覆盖
          if (this.comicInfo && this.comicInfo.id) {
            this.comicInfo = { ...this.comicInfo, ...data.comic };
          } else {
            this.comicInfo = data.comic;
          }
          console.log('[ComicReader] 漫画信息:', JSON.stringify(this.comicInfo));
          
          // 更新内部漫画ID
          if (this.comicInfo.id && !this.internalComicId) {
            this.internalComicId = this.comicInfo.id;
            console.log('[ComicReader] 更新内部漫画ID:', this.internalComicId);
          }
        }
        
        // 确保有漫画ID
        if (!this.comicInfo || !this.comicInfo.id) {
          console.error('[ComicReader] 处理完成后仍然无法获取漫画ID');
          this.isLoading = false;
          return false;
        }
        
        // 保存访问信息
        if (data.accessInfo) {
          this.accessInfo = data.accessInfo;
          console.log('[ComicReader] 访问信息:', JSON.stringify(this.accessInfo));
        }
        
        // 检查是否可以访问
        if (data.accessInfo && !data.accessInfo.canAccess) {
          console.log('[ComicReader] 无法访问章节，原因:', data.accessInfo.reason);
          this.isLoading = false;
          return false;
        }
        
        // 处理内容数据
        let contentArray = [];
        console.log('[ComicReader] 内容数据类型:', typeof data.content, 
                    '是否为数组:', Array.isArray(data.content));
        
        if (data.content && data.content.content) {
          contentArray = data.content.content;
          console.log('[ComicReader] 内容来自content.content字段，长度:', contentArray.length);
        } else if (Array.isArray(data.content)) {
          contentArray = data.content;
          console.log('[ComicReader] 内容来自content数组字段，长度:', contentArray.length);
        } else if (typeof data.content === 'object') {
          console.log('[ComicReader] 内容字段是对象，键名:', Object.keys(data.content).join(', '));
          if (data.content.images && Array.isArray(data.content.images)) {
            contentArray = data.content.images;
            console.log('[ComicReader] 内容来自content.images字段，长度:', contentArray.length);
          }
        }
        
        if (contentArray.length === 0) {
          console.error('[ComicReader] 无法获取章节图片内容');
          console.error('[ComicReader] 内容数据结构:', JSON.stringify(data.content));
          this.isLoading = false;
          return false;
        }
        
        console.log('[ComicReader] 内容信息:', {
          contentLength: contentArray.length,
          contentSample: contentArray.slice(0, 2)
        });
        
        // 更新总页数
        this.totalPages = contentArray.length;
        
        // 根据需要处理图片URL
        const processedImages = contentArray.map((img, index) => {
          if (typeof img !== 'string') {
            console.error(`[ComicReader] 图片${index}不是字符串:`, img);
            return '';
          }
          
          // 如果是相对路径，添加基础URL
          if (img && !img.startsWith('http')) {
            return BASE_URL + img;
          }
          return img;
        }).filter(img => img); // 过滤掉空字符串
        
        if (processedImages.length === 0) {
          console.error('[ComicReader] 处理后的图片数组为空');
          this.isLoading = false;
          return false;
        }
        
        console.log('[ComicReader] 状态更新完成 - 标题:', this.comicInfo?.title || '未知');
        console.log('[ComicReader] 状态更新完成 - 总页数:', this.totalPages);
        console.log('[ComicReader] 原始图片URLs样本:', contentArray.slice(0, 2));
        console.log('[ComicReader] 处理后图片URLs样本:', processedImages.slice(0, 2));
        console.log('[ComicReader] 是否为免费章节:', this.chapterInfo?.is_free ? true : false);
        console.log('[ComicReader] 访问原因:', this.accessInfo?.reason || '无');
        
        // 更新图片数组
        this.chapterImages = processedImages;
        this.loadedImagesCount = 0;
        
        // 重置当前页为第一页
        if (this.currentPage > this.totalPages) {
          this.currentPage = 1;
        }

        this.isLoading = false;

        if (this.viewMode === 'continuous') {
          setTimeout(() => {
            if (this.isLoading) {
              this.isLoading = false;
            }
          }, 600);
        }

        // 章节加载完成后，尝试恢复阅读进度
        setTimeout(() => {
          this.restoreReadingProgress();
        }, 500);

        console.log('[ComicReader] 章节加载完成状态:', true);
        return true;
      } catch (e) {
        console.error('[ComicReader] 处理章节数据出错:', e);
        console.error('[ComicReader] 错误详情:', e.message);
        console.error('[ComicReader] 错误堆栈:', e.stack);
        this.isLoading = false;
        return false;
      }
    },
    // 切换阅读模式：单页/连续长图
    toggleViewMode() {
      this.viewMode = this.viewMode === 'single' ? 'continuous' : 'single';
      localStorage.setItem('comicViewMode', this.viewMode);
      if (this.viewMode === 'continuous') {
        this.loadedImagesCount = 0;
      }
      this.applyBodyClass();
    },
    applyBodyClass() {
      if (this.viewMode === 'continuous') {
        document.body.classList.add('reader-continuous-mode');
      } else {
        document.body.classList.remove('reader-continuous-mode');
      }
    },
    openReportModal() {
      this.reportForm = { type: '', content: '', contact: '' };
      this.showReportModal = true;
    },
    closeReportModal() {
      this.showReportModal = false;
    },
    async submitReport() {
      if (!this.reportForm.type) {
        alert('请选择举报类型');
        return;
      }
      if (!this.reportForm.content.trim()) {
        alert('请填写详细描述');
        return;
      }

      this.reportSubmitting = true;
      // 当前为前端模拟，直接提示成功
      setTimeout(() => {
        this.reportSubmitting = false;
        alert('举报提交成功，我们会尽快处理');
        this.closeReportModal();
      }, 500);
    },
    
    // 加载阅读模式设置
    loadViewModeSettings() {
      const savedMode = localStorage.getItem('comicViewMode');
      if (savedMode) {
        this.viewMode = savedMode;
      } else {
        this.viewMode = 'continuous';
        localStorage.setItem('comicViewMode', 'continuous');
      }
      this.applyBodyClass();
    },
    
    // 切换全屏模式
    async toggleFullscreen() {
      try {
        if (!this.isFullscreen) {
          const elem = document.documentElement;
          if (elem.requestFullscreen) {
            await elem.requestFullscreen();
          } else if (elem.mozRequestFullScreen) { /* Firefox */
            await elem.mozRequestFullScreen();
          } else if (elem.webkitRequestFullscreen) { /* Chrome, Safari & Opera */
            await elem.webkitRequestFullscreen();
          } else if (elem.msRequestFullscreen) { /* IE/Edge */
            await elem.msRequestFullscreen();
          }
        } else {
          if (document.exitFullscreen) {
            await document.exitFullscreen();
          } else if (document.mozCancelFullScreen) { /* Firefox */
            await document.mozCancelFullScreen();
          } else if (document.webkitExitFullscreen) { /* Chrome, Safari & Opera */
            await document.webkitExitFullscreen();
          } else if (document.msExitFullscreen) { /* IE/Edge */
            await document.msExitFullscreen();
          }
        }
      } catch (error) {
        console.error('[ComicReader] 全屏模式切换失败:', error);
      }
    },
    
    // 处理全屏状态变化
    handleFullscreenChange() {
      this.isFullscreen = !!document.fullscreenElement;
    },
    
    // 切换头部和底部控制栏显示
    toggleHeader() {
      this.isHeaderHidden = !this.isHeaderHidden;
    },
    
    // 处理连续模式下的图片加载
    // eslint-disable-next-line no-unused-vars
    onContinuousImageLoad(index) {
      this.loadedImagesCount++;
      if (this.loadedImagesCount >= this.chapterImages.length) {
        this.isLoading = false;
      }
    },
    
    // 获取上一章节ID
    getPrevChapterId() {
      const currentIndex = this.chapters.findIndex(ch => ch.id === parseInt(this.currentChapterId));
      if (currentIndex > 0) {
        return this.chapters[currentIndex - 1].id;
      }
      return null;
    },
    
    // 获取下一章节ID
    getNextChapterId() {
      const currentIndex = this.chapters.findIndex(ch => ch.id === parseInt(this.currentChapterId));
      if (currentIndex < this.chapters.length - 1) {
        return this.chapters[currentIndex + 1].id;
      }
      return null;
    },
    
    // 跳转到指定章节
    goToChapter(chapterId) {
      if (!chapterId) return;
      this.selectChapter(chapterId);
    },

    // 处理章节选择下拉框的变化
    onChapterSelectChange(event) {
      const selectedChapterId = Number(event.target.value);
      console.log('[ComicReader] 下拉框选择事件触发');
      console.log('[ComicReader] 选择的章节ID:', selectedChapterId, '类型:', typeof selectedChapterId);
      console.log('[ComicReader] 当前章节ID:', this.currentChapterId, '类型:', typeof this.currentChapterId);
      console.log('[ComicReader] 可用章节列表:', this.chapters.map(ch => ({ id: ch.id, title: ch.title })));

      if (selectedChapterId && selectedChapterId !== Number(this.currentChapterId)) {
        console.log('[ComicReader] 条件满足，执行章节切换');
        // 直接调用selectChapter方法
        this.selectChapter(selectedChapterId);
      } else {
        console.log('[ComicReader] 章节ID相同或无效，跳过切换');
        console.log('[ComicReader] selectedChapterId:', selectedChapterId);
        console.log('[ComicReader] currentChapterId:', Number(this.currentChapterId));
        console.log('[ComicReader] 相等比较结果:', selectedChapterId === Number(this.currentChapterId));
      }
    },
    
    // 获取用户VIP状态
    async checkVipStatus() {
      try {
        const token = localStorage.getItem('userToken');
        if (!token) {
          this.userVipStatus = { isVip: false, endDate: null };
          return;
        }
        
        const res = await getUserVipInfo();
        if (res.data && res.data.code === 1) {
          this.userVipStatus = res.data.data;
          console.log('[ComicReader] 用户VIP状态:', this.userVipStatus.isVip ? 'VIP用户' : '普通用户');
        }
      } catch (e) {
        console.error('[ComicReader] 获取VIP状态失败', e);
        this.userVipStatus = { isVip: false, endDate: null };
      }
    },
    // 下一页
    nextPage() {
      console.log('下一页', this.currentPage, '->', this.currentPage + 1);
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        this.saveReadingProgress();
        this.scrollToTop();
      }
    },
    
    // 上一页
    prevPage() {
      console.log('上一页', this.currentPage, '->', this.currentPage - 1);
      if (this.currentPage > 1) {
        this.currentPage--;
        this.saveReadingProgress();
        this.scrollToTop();
      }
    },
    
    // 跳转到指定页
    goToPage(page) {
      console.log('跳转页码', this.currentPage, '->', page);
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
        this.saveReadingProgress();
        this.scrollToTop();
      }
    },
    
    // 重新开始阅读
    restartReading() {
      console.log('重新开始阅读');
      this.currentPage = 1;
      this.saveReadingProgress();
      this.scrollToTop();
    },
    
    // 滚动到顶部
    scrollToTop() {
      if (this.$refs.comicContent) {
        this.$refs.comicContent.scrollTop = 0;
      }
    },
    
    // 图片加载完成
    onImageLoad() {
      this.isLoading = false;
      this.imageLoaded = true;
      console.log('[ComicReader] 图片加载完成 - 页码:', this.currentPage);
      console.log('[ComicReader] 图片URL:', this.chapterImages[this.currentPage - 1]);
    },
    
    // 图片加载失败
    onImageError() {
      this.isLoading = false;
      console.error(`[ComicReader] 图片加载失败: 第${this.currentPage}页`);
      console.error('[ComicReader] 失败的图片URL:', this.chapterImages[this.currentPage - 1]);
      
      // 尝试分析失败原因
      const imgUrl = this.chapterImages[this.currentPage - 1];
      if (!imgUrl) {
        console.error('[ComicReader] 错误原因: 图片URL为空');
      } else if (!imgUrl.startsWith('http')) {
        console.error('[ComicReader] 错误原因: 图片URL不是完整的HTTP地址:', imgUrl);
      } else {
        console.error('[ComicReader] 可能是网络问题或服务器图片不存在');
      }
    },
    
    // 添加键盘事件监听
    addKeyboardListeners() {
      document.addEventListener('keydown', this.handleKeydown);
    },
    
    // 移除键盘事件监听
    removeKeyboardListeners() {
      document.removeEventListener('keydown', this.handleKeydown);
    },
    
    // 处理键盘事件
    handleKeydown(event) {
      if (this.viewMode !== 'single') {
        return;
      }
      switch(event.key) {
        case 'ArrowRight':
        case ' ':
          event.preventDefault();
          this.nextPage();
          break;
        case 'ArrowLeft':
          event.preventDefault();
          this.prevPage();
          break;
        case 'Home':
          event.preventDefault();
          this.goToPage(1);
          break;
        case 'End':
          event.preventDefault();
          this.goToPage(this.totalPages);
          break;
      }
    },
    
    // 保存阅读进度
    saveReadingProgress() {
      console.log('保存阅读进度', this.currentPage);
      const progress = {
        comicId: this.$route.params.id,
        currentPage: this.currentPage,
        timestamp: Date.now()
      };
      localStorage.setItem('comicReadingProgress', JSON.stringify(progress));
      
      // 保存阅读历史
      this.saveReadingHistory();
    },
    
    // 保存阅读历史
    saveReadingHistory() {
      console.log('保存阅读历史', this.comicTitle, this.currentPage);
      const comicId = this.internalComicId || this.$route.params.id;
      
      // 获取封面图片
      let coverUrl = this.comicInfo?.cover || '';
      if (!coverUrl && this.internalComicId) {
        coverUrl = `/uploads/comics/${this.internalComicId}/cover.jpg`;
      }
      
      // 使用 ReadingProgressManager 保存
      ReadingProgressManager.saveProgress(
        comicId,
        this.chapterId,
        this.chapterInfo?.title || `第${this.chapterId}话`,
        this.comicTitle || '未知漫画',
        this.currentPage,
        this.totalPages,
        coverUrl
      );
      
      console.log('阅读历史保存成功');
    },
    async selectChapter(chapterId) {
      console.log('[ComicReader] selectChapter调用，章节ID:', chapterId);
      console.log('[ComicReader] 当前章节ID:', this.currentChapterId);

      // 转换为数字进行比较
      const targetChapterId = Number(chapterId);
      const currentChapterId = Number(this.currentChapterId);

      if (targetChapterId === currentChapterId) {
        console.log('[ComicReader] 章节ID相同，跳过切换');
        return;
      }

      console.log('[ComicReader] 切换章节:', targetChapterId);

      // 设置标志，防止路由监听器重复加载
      this.isSelectingChapter = true;
      // 获取章节信息并记录到日志中
      const selectedChapter = this.chapters.find(ch => ch.id === targetChapterId);
      if (selectedChapter) {
        console.log('[ComicReader] 选中章节信息:',
          `标题: ${selectedChapter.title}, ` +
          `是否免费: ${selectedChapter.is_free ? '是' : '否'}`
        );
      }

      // 保存当前选中的章节ID
      this.currentChapterId = targetChapterId;

      // 重置页码
      this.currentPage = 1;

      // 加载章节并获取结果（是否成功加载）
      const chapterLoaded = await this.loadChapter(targetChapterId);
      
      // 只有章节成功加载时才更新URL
      if (chapterLoaded) {
        // 先滚动回顶部
        this.$nextTick(() => {
          this.scrollToTop();
        });
        // 更新URL而不刷新页面，确保传递comicId参数
        const routeParams = { id: targetChapterId.toString() };
        if (this.internalComicId) {
          routeParams.comicId = this.internalComicId.toString();
        }

        console.log('[ComicReader] 更新路由参数:', routeParams);

        // 使用replace而不是push，避免在浏览器历史中创建新条目
        this.$router.replace({
          name: 'ComicReader',
          params: routeParams
        }).catch(err => {
          // 忽略导航重复错误
          if (err.name !== 'NavigationDuplicated') {
            console.error('[ComicReader] 路由更新失败:', err);
          }
        });
      } else {
        console.error('[ComicReader] 章节加载失败，不更新路由');
      }
    },
    
    // 前往个人中心
    goToProfile() {
      this.$router.push('/profile');
    },
    
    // 返回上一页
    goBack() {
      this.$router.go(-1);
    },

    // 获取用户信息
    async fetchUserInfo() {
      try {
        const token = localStorage.getItem('userToken');
        if (!token) return;
        
        const res = await getUserInfo();
        if (res.data && res.data.code === 1) {
          this.userInfo = res.data.data;
          console.log('[ComicReader] 用户信息获取成功:', 
            `用户名: ${this.userInfo.username}, ` +
            `金币: ${this.userInfo.coins}, ` + 
            `是否VIP: ${this.userInfo.is_vip ? '是' : '否'}`
          );
        }
      } catch (e) {
        console.error('[ComicReader] 获取用户信息失败', e);
      }
    },
    
    // 使用金币购买章节
    async buyChapterWithCoins() {
      try {
        // 检查是否已登录
        const token = localStorage.getItem('userToken');
        console.log('[ComicReader] 购买章节 - 用户Token:', token ? '已提供' : '未提供');
        
        if (!token) {
          console.error('[ComicReader] 购买失败: 未登录');
          alert('请先登录后再购买章节');
          return;
        }
        
        // 检查章节ID是否有效
        if (!this.chapterInfo || !this.chapterInfo.id) {
          console.error('[ComicReader] 购买失败: 无效的章节ID');
          alert('无法购买，章节信息不完整');
          return;
        }
        
        const chapterId = this.chapterInfo.id;
        console.log('[ComicReader] 购买章节 - 章节ID:', chapterId, '类型:', typeof chapterId);
        
        // 检查金币是否足够
        const chapterPrice = this.chapterInfo.price || 3;
        console.log('[ComicReader] 购买章节 - 章节价格:', chapterPrice);
        console.log('[ComicReader] 购买章节 - 用户金币:', this.userInfo ? this.userInfo.coins : '未知');
        
        if (!this.userInfo || this.userInfo.coins < chapterPrice) {
          alert(`金币不足！购买本章节需要${chapterPrice}金币，您当前拥有${this.userInfo ? this.userInfo.coins : 0}金币。`);
          return;
        }
        
        // 显示加载状态
        this.isLoading = true;

        // 青少年模式购买拦截
        const purchaseCheck = await youthModeManager.checkBeforePurchase();
        if (!purchaseCheck.allowed) {
          this.isLoading = false;
          alert(purchaseCheck.message);
          return;
        }

        console.log('[ComicReader] 开始购买章节，章节ID:', chapterId);
        
        // 调用购买章节API
        try {
          const response = await buyChapter(chapterId);
          console.log('[ComicReader] 购买API返回结果:', JSON.stringify(response.data));
          
          if (response.data && response.data.code === 1) {
            console.log('[ComicReader] 章节购买成功:', response.data.msg);
            
            // 更新用户金币余额
            if (this.userInfo) {
              this.userInfo.coins -= chapterPrice;
              console.log('[ComicReader] 更新后的金币余额:', this.userInfo.coins);
            }
            
            // 关闭VIP限制提示
            this.showVipLimitMsg = false;
            
            // 重新加载章节
            await this.loadChapter(chapterId);
            
            // 显示购买成功提示
            alert(`购买成功，已扣除${chapterPrice}金币`);
          } else {
            console.error('[ComicReader] 购买失败:', response.data);
            alert(response.data.msg || '购买失败，请重试');
          }
        } catch (apiError) {
          console.error('[ComicReader] API调用失败:', apiError);
          
          if (apiError.response && apiError.response.status === 401) {
            console.error('[ComicReader] 401错误 - 未授权访问');
            alert('登录已过期，请重新登录后再购买');
          } else {
            alert(`购买失败: ${apiError.message}`);
          }
        }
        
        this.isLoading = false;
      } catch (e) {
        console.error('[ComicReader] 购买章节失败', e);
        alert('购买失败，请检查网络连接后重试');
        this.isLoading = false;
      }
    },
    // 尝试通过章节ID获取相关信息
    async fetchChapterInfo() {
      try {
        console.log('[ComicReader] 尝试通过章节ID获取相关信息:', this.chapterId);
        const res = await getChapterDetail(String(this.chapterId));
        
        if (res.data && res.data.code === 1) {
          const data = res.data.data;
          
          // 从章节信息中提取漫画ID
          if (data.chapter && data.chapter.comic_id) {
            this.internalComicId = data.chapter.comic_id;
            console.log('[ComicReader] 从章节信息中提取漫画ID:', this.internalComicId);
            
            // 获取章节列表
            await this.fetchChapters();
            
            // 设置当前章节ID
            this.currentChapterId = this.chapterId;
            console.log('[ComicReader] 设置当前章节ID:', this.currentChapterId);
            
            return true;
          }
        }
        
        return false;
      } catch (e) {
        console.error('[ComicReader] 通过章节ID获取相关信息失败:', e);
        return false;
      }
    },

    // ========== 阅读进度相关方法 ==========

    // 恢复阅读进度
    async restoreReadingProgress() {
      if (!this.chapterId || !this.internalComicId) {
        console.log('[ReadingProgress] 无法恢复进度：缺少章节ID或漫画ID');
        return;
      }

      try {
        const token = localStorage.getItem('userToken');
        let progress = null;

        if (token) {
          const res = await getReadingProgress(this.internalComicId);
          if (res.data && res.data.code === 1 && res.data.data) {
            progress = {
              currentPage: res.data.data.current_page,
              chapterId: res.data.data.chapter_id
            };
          }
        }

        // 未登录或后端无记录时尝试本地记录
        if (!progress) {
          progress = ReadingProgressManager.getComicProgress(this.internalComicId, this.chapterId);
        }

        if (progress && progress.currentPage > 1) {
          console.log('[ReadingProgress] 恢复阅读进度:', progress);

          // 延迟恢复页码，确保章节内容已加载
          setTimeout(() => {
            if (progress.currentPage <= this.totalPages) {
              this.currentPage = progress.currentPage;
              this.lastSavedPage = progress.currentPage;
              this.isProgressLoaded = true;
              console.log('[ReadingProgress] 进度恢复成功，页码:', this.currentPage);
            }
          }, 1000);
        }
      } catch (error) {
        console.error('[ReadingProgress] 恢复阅读进度失败:', error);
      }
    },

    // 保存当前阅读进度
    async saveCurrentProgress() {
      if (!this.chapterId || !this.internalComicId || !this.comicTitle) {
        console.log('[ReadingProgress] 无法保存进度：缺少必要信息');
        return;
      }

      // 避免频繁保存相同页码
      if (this.currentPage === this.lastSavedPage) {
        return;
      }

      const token = localStorage.getItem('userToken');
      const chapterTitle = this.chapterInfo?.title || `第${this.chapterId}话`;
      let coverImage = this.comicInfo?.cover || '';

      // 处理封面图片URL，确保是完整路径
      if (coverImage && !coverImage.startsWith('http')) {
        coverImage = BASE_URL + coverImage;
      }

      try {
        if (token) {
          const res = await saveReadingProgress({
            comic_id: this.internalComicId,
            chapter_id: this.chapterId,
            current_page: this.currentPage,
            total_pages: this.totalPages,
            chapter_title: chapterTitle,
            comic_title: this.comicTitle,
            cover: coverImage
          });
          if (res.data && res.data.code === 1) {
            this.lastSavedPage = this.currentPage;
            console.log('[ReadingProgress] 后端进度保存成功');
          }
        } else {
          const progress = ReadingProgressManager.saveProgress(
            this.internalComicId,
            this.chapterId,
            chapterTitle,
            this.comicTitle,
            this.currentPage,
            this.totalPages,
            coverImage
          );
          if (progress) {
            this.lastSavedPage = this.currentPage;
            console.log('[ReadingProgress] 本地进度保存成功:', progress);
          }
        }
      } catch (error) {
        console.error('[ReadingProgress] 保存阅读进度失败:', error);
      }
    },

    // 启动自动保存进度
    startAutoSaveProgress() {
      this.stopAutoSaveProgress(); // 先清理现有定时器

      this.progressSaveTimer = setInterval(() => {
        this.saveCurrentProgress();
      }, this.autoSaveInterval);

      console.log('[ReadingProgress] 启动自动保存，间隔:', this.autoSaveInterval, 'ms');
    },

    // 停止自动保存进度
    stopAutoSaveProgress() {
      if (this.progressSaveTimer) {
        clearInterval(this.progressSaveTimer);
        this.progressSaveTimer = null;
        console.log('[ReadingProgress] 停止自动保存');
      }
    }
  },
  watch: {
    // 监听页码变化，显示加载状态
    currentPage(newPage, oldPage) {
      this.isLoading = true;
      this.imageLoaded = false;

      // 页码变化时保存阅读进度
      if (newPage !== oldPage && this.isProgressLoaded) {
        // 延迟保存，避免快速翻页时频繁保存
        setTimeout(() => {
          this.saveCurrentProgress();
        }, 2000);
      }
    },
    currentChapterId(newChapterId, oldChapterId) {
      console.log('[ComicReader] currentChapterId变化:', oldChapterId, '->', newChapterId);
      // 这里不执行selectChapter，避免无限循环
      // selectChapter方法会自己更新currentChapterId
    },
    // 监听路由变化
    '$route'(to, from) {
      console.log('[ComicReader] 路由变化检测');
      console.log('[ComicReader] 从:', from.params);
      console.log('[ComicReader] 到:', to.params);

      // 如果章节ID发生变化，重新加载章节
      if (to.params.id !== from.params.id && to.params.id) {
        console.log('[ComicReader] 章节ID变化，重新加载章节');

        // 防止在selectChapter方法中触发的路由变化再次加载
        if (this.isSelectingChapter) {
          console.log('[ComicReader] 跳过路由监听触发的加载（由selectChapter触发）');
          this.isSelectingChapter = false;
          return;
        }

        this.chapterId = to.params.id;
        this.currentChapterId = to.params.id;

        // 更新漫画ID（如果有的话）
        if (to.params.comicId) {
          this.internalComicId = to.params.comicId;
        }

        // 重置页码
        this.currentPage = 1;

        // 重新加载章节内容
        this.loadChapter(to.params.id);
      }
    }
  }
}
</script>

<style scoped>
/* 阅读器容器 */
.comic-reader {
  width: 100%;
  height: calc(100vh - 0px);
  display: flex;
  flex-direction: column;
  position: relative;
  background: #0a0a0a;
  overflow: hidden;
}

/* 连续模式：容器不限高度，由页面自然滚动 */
.comic-reader.continuous-reader {
  height: auto;
  min-height: calc(100vh - 0px);
  overflow: visible;
}

.comic-reader.continuous-reader .comic-content {
  height: auto;
  overflow: visible;
}

/* 全屏模式 */
.fullscreen-mode {
  width: 100vw;
  height: 100vh;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 9999;
}

/* 隐藏控制栏 */
.hidden {
  opacity: 0;
  pointer-events: none;
}

/* 头部导航栏 */
.reader-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10;
  transition: opacity 0.3s ease;
}

/* 底部控制栏 */
.reader-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 10;
  transition: opacity 0.3s ease;
}

/* 模式切换按钮 */
.view-controls {
  display: flex;
  gap: 10px;
}

.view-mode-btn,
.fullscreen-btn {
  color: white;
  border: none;
  border-radius: 8px;
  padding: 8px 14px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.view-mode-btn {
  background: var(--brand);
}

.view-mode-btn:hover {
  background: var(--brand-hover);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(79, 172, 254, 0.4);
}

.fullscreen-btn {
  background: var(--brand);
}

.fullscreen-btn:hover {
  background: var(--brand);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

/* 漫画内容区域 */
.comic-content {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  max-width: none;
  height: 100%;
  overflow: auto;
  position: relative;
  background: #0a0a0a;
  padding: 0 20px;
}

/* 连续模式内容区域 */
.continuous-mode {
  padding: 0;
  overflow: visible;
  display: block;
  height: auto;
  align-items: stretch;
}

/* 单页模式图片：统一缩放到容器宽度 */
.comic-page {
  width: 100%;
  height: auto;
  object-fit: contain;
  display: block;
  margin: 0 auto;
}

/* 漫画图片容器：固定目标宽度 700px，小图也会放大到该宽度 */
.comic-page-wrapper,
.continuous-page-wrapper {
  position: relative;
  display: block;
  line-height: 0;
  width: 700px;
  max-width: 100%;
  margin: 0 auto;
}

/* 连续模式（长图）样式 */
.continuous-view {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.continuous-page {
  width: 100%;
  height: auto;
  display: block;
  margin: 0 auto;
}

/* 水印元素样式 */
.watermark {
  position: absolute;
  font-weight: bold;
  color: rgba(255, 255, 255, 0.5);
  pointer-events: none;
  z-index: 10;
  letter-spacing: 2px;
}

.watermark-left-bottom {
  bottom: 20px;
  left: 20px;
  font-size: 18px;
}

.watermark-right-bottom {
  bottom: 20px;
  right: 20px;
  font-size: 18px;
}

/* 结束页面 */
.end-page {
  padding: 50px;
  text-align: center;
  color: white;
  width: 100%;
  max-width: 800px;
  margin: 20px auto;
}

.end-page h2 {
  font-size: 24px;
  margin-bottom: 16px;
}

.restart-btn {
  background: var(--brand);
  color: white;
  border: none;
  border-radius: 24px;
  padding: 12px 24px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 20px;
}

.restart-btn:hover {
  background: var(--brand-hover);
  transform: translateY(-1px);
}

/* 导航按钮 */
.nav-btn {
  background: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  cursor: pointer;
  transition: all 0.2s;
}

.nav-btn:hover:not(:disabled) {
  background: rgba(0, 0, 0, 0.8);
}

.nav-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* 页码控制 */
.page-controls {
  display: flex;
  gap: 8px;
  align-items: center;
}

.page-btn {
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s;
}

.page-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.page-btn.active {
  background: var(--brand);
}

.page-dots {
  color: white;
  font-size: 14px;
}

/* 加载动画 */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  color: white;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-top: 4px solid var(--brand);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* VIP限制提示 */
.vip-limit-message {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  color: white;
}

.vip-alert {
  background: rgba(0, 0, 0, 0.8);
  padding: 40px;
  border-radius: 8px;
  text-align: center;
  box-shadow: 0 0 20px rgba(59, 130, 246, 0.3);
  border: 1px solid var(--brand);
}

.vip-alert h3 {
  font-size: 24px;
  margin-bottom: 16px;
  color: var(--brand);
}

.vip-alert p {
  font-size: 18px;
  margin-bottom: 32px;
}

/* 金币信息 */
.coins-info {
  background-color: var(--brand-soft);
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 20px;
  border: 1px dashed var(--brand);
}

.coins-info p {
  margin: 8px 0;
  font-size: 16px;
}

.coin-balance, .coin-cost {
  color: var(--brand);
  font-weight: bold;
  font-size: 18px;
}

.coin-cost {
  color: var(--brand);
}

.vip-buttons {
  display: flex;
  justify-content: center;
  gap: 16px;
}

.go-vip-btn, .back-btn, .buy-btn {
  background: var(--brand);
  color: white;
  border: none;
  border-radius: 24px;
  padding: 12px 24px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s;
}

.buy-btn {
  background: var(--brand);
  color: white;
  border: none;
  border-radius: 24px;
  padding: 12px 24px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s;
}

.buy-btn:hover {
  background: var(--brand-hover);
  transform: translateY(-1px);
}

.go-vip-btn:hover, .back-btn:hover {
  background: var(--brand-hover);
  transform: translateY(-1px);
}

/* 章节导航按钮 */
.chapter-nav-buttons {
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-top: 30px;
  margin-bottom: 50px;
}

.chapter-nav-btn {
  background: var(--brand);
  color: white;
  border: none;
  border-radius: 24px;
  padding: 12px 24px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s;
}

.chapter-nav-btn:hover {
  background: var(--brand-hover);
  transform: translateY(-1px);
}

/* 左下角工具按钮 */
.reader-tools {
  position: fixed;
  left: 16px;
  bottom: 24px;
  z-index: 20;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.tool-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border: none;
  border-radius: 20px;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
}

.tool-btn:hover {
  background: rgba(0, 0, 0, 0.8);
  transform: translateY(-1px);
}

.report-btn {
  background: rgba(245, 108, 108, 0.85);
}

.report-btn:hover {
  background: rgba(245, 108, 108, 1);
}

.tool-icon {
  width: 15px;
  height: 15px;
  display: inline-block;
}

/* 投诉举报弹窗 */
.report-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.65);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
  padding: 20px;
}

.report-modal {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 16px;
  width: 100%;
  max-width: 520px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);
}

.report-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px;
  border-bottom: 1px solid var(--border);
}

.report-header h3 {
  margin: 0;
  font-size: 18px;
  color: var(--text);
}

.report-close {
  width: 32px;
  height: 32px;
  border: none;
  background: var(--surface-3);
  border-radius: 50%;
  font-size: 18px;
  cursor: pointer;
  color: var(--text-dim);
  display: flex;
  align-items: center;
  justify-content: center;
}

.report-close:hover {
  background: var(--brand-soft);
  color: var(--brand);
}

.report-body {
  padding: 18px 20px;
}

.report-info {
  background: var(--surface-2);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 14px;
}

.report-info p {
  margin: 6px 0;
  font-size: 13px;
  color: var(--text-dim);
}

.report-info strong {
  color: var(--text);
}

.form-group {
  margin-bottom: 14px;
}

.form-group label {
  display: block;
  margin-bottom: 6px;
  font-size: 14px;
  color: var(--text);
  font-weight: 500;
}

.form-group select,
.form-group textarea,
.form-group input {
  width: 100%;
  padding: 10px 12px;
  background: var(--surface-2);
  border: 1px solid var(--border);
  border-radius: 10px;
  font-size: 14px;
  outline: none;
  box-sizing: border-box;
  color: var(--text);
}

.form-group select:focus,
.form-group textarea:focus,
.form-group input:focus {
  border-color: var(--brand);
}

.report-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 20px 18px;
}

.cancel-btn {
  padding: 10px 18px;
  background: var(--surface-3);
  border: 1px solid var(--border);
  border-radius: 10px;
  cursor: pointer;
  font-size: 14px;
  color: var(--text-dim);
}

.submit-btn {
  padding: 10px 18px;
  border: none;
  background: var(--brand);
  color: white;
  border-radius: 10px;
  cursor: pointer;
  font-size: 14px;
}

.submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* 快捷键提示 */
.shortcut-hint {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 70px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 16px;
  padding: 10px 16px;
  color: rgba(255, 255, 255, 0.85);
  font-size: 12px;
  z-index: 15;
  pointer-events: none;
}

.shortcut-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
}

.shortcut-item kbd {
  display: inline-block;
  padding: 2px 6px;
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.35);
  font-family: inherit;
  font-size: 11px;
  line-height: 1.2;
  color: #fff;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .reader-header, 
  .reader-controls {
    flex-direction: column;
    gap: 10px;
  }
  
  .view-controls {
    width: 100%;
    justify-content: center;
  }
  
  .page-controls {
    margin: 10px 0;
  }
  
  .chapter-select {
    width: 100%;
  }
  
  .continuous-page {
    max-width: 100%;
  }

  .shortcut-hint {
    flex-direction: column;
    align-items: center;
    gap: 6px;
    padding: 10px;
  }

  .shortcut-item {
    font-size: 11px;
  }

  .shortcut-item kbd {
    font-size: 10px;
    padding: 1px 5px;
  }
}
</style> 